import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { UnifiedMarkdownRenderer } from "@/components/ui/unified-markdown-renderer";
import { AlignLeft, Eye, Upload, Loader2 } from "lucide-react";
import { TemplateFormData, TemplatePermissions } from "../types";
import { Template } from "@/types";
import AdvancedSettingsToggle from "./AdvancedSettingsToggle";
import { User } from "@supabase/supabase-js";
import { documentService } from "@/integrations/documents/documentService";
import { fileToBase64, validateDocumentFile } from "@/lib/file-utils";
import { useTranslation } from "react-i18next";

interface TemplateFormProps {
  formData: TemplateFormData;
  onUpdateField: (field: keyof TemplateFormData, value: string, options?: { markPromptTouched?: boolean }) => void;
  permissions: TemplatePermissions;
  user?: User | null;
  baseTemplateId?: string | null;
  baseTemplate?: Template | null;
  showAdvanced?: boolean;
  onShowAdvancedChange?: (open: boolean) => void;
  isPromptTouched?: boolean;
  isAiInstructionDirty?: boolean;
  isMobile?: boolean;
  dataLoadedKey?: number;
}

const TemplateForm = ({
  formData,
  onUpdateField,
  permissions,
  user,
  baseTemplateId,
  baseTemplate,
  showAdvanced = false,
  onShowAdvancedChange = () => { },
  isPromptTouched = false,
  isAiInstructionDirty = false,
  isMobile = false,
  dataLoadedKey = 0,
}: TemplateFormProps) => {
  const { t } = useTranslation();
  const { description, previewTemplateResult, prompt } = formData;
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState<'idle' | 'converting' | 'processing' | 'completed'>('idle');
  const [fileUploadKey, setFileUploadKey] = useState(0);


  const handlePromptChange = (value: string, options?: { markPromptTouched?: boolean }) => {
    onUpdateField("prompt", value, { markPromptTouched: options?.markPromptTouched ?? true });
  };

  const getUploadStageText = () => {
    switch (uploadStage) {
      case 'converting':
        return t('templates.form.status.converting');
      case 'processing':
        return t('templates.form.status.processing');
      case 'completed':
        return t('templates.form.status.completed');
      default:
        return t('templates.form.status.uploading');
    }
  };

  const getUploadStageDescription = () => {
    switch (uploadStage) {
      case 'converting':
        return t('templates.form.status_desc.converting');
      case 'processing':
        return t('templates.form.status_desc.processing');
      case 'completed':
        return t('templates.form.status_desc.completed');
      default:
        return t('templates.form.status_desc.processing_file');
    }
  };

  const handleFileUpload = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.docx,.pdf,.odt';

    input.onchange = async (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) return;

      // Валидация файла
      const validation = validateDocumentFile(file);
      if (!validation.valid) {
        alert(validation.error);
        return;
      }

      setIsUploading(true);
      setUploadProgress(0);
      setUploadStage('converting');

      try {
        // Эмуляция прогресса конвертации в base64
        setUploadProgress(25);

        // Конвертация в base64
        const base64Content = await fileToBase64(file);

        setUploadProgress(50);
        setUploadStage('processing');

        // Конвертация в markdown
        const response = await documentService.convertToMarkdown({
          filename: file.name,
          file_content: base64Content
        });

        setUploadProgress(90);
        setUploadStage('completed');

        if (response.success && response.markdown_text) {
          setUploadProgress(100);
          // Обновляем ключ для принудительного перерендеринга компонента
          setFileUploadKey(prev => prev + 1);
          // Загрузка результата в секцию "Пример документа"
          onUpdateField("previewTemplateResult", response.markdown_text);
        } else {
          throw new Error(t('templates.form.error_convert'));
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        alert(`${t('templates.form.error_upload')}: ${error instanceof Error ? error.message : 'неизвестная ошибка'}`);
      } finally {
        setTimeout(() => {
          setIsUploading(false);
          setUploadProgress(0);
          setUploadStage('idle');
        }, 500);
      }
    };

    input.click();
  };

  return (
    <div className="w-full flex flex-col space-y-6 h-full md:h-auto">
      {baseTemplateId && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-medium text-blue-900 mb-1">{t('templates.form.create_from_existing_title')}</h3>
          <p className="text-sm text-blue-700">
            {t('templates.form.create_from_existing_desc')}
          </p>
        </div>
      )}


      {/* Описание результата */}
      <div className="space-y-2">
        <Label htmlFor="description" className="flex items-center gap-2">
          <AlignLeft size={16} className="text-muted-foreground" />
          {t('templates.form.result_desc_label')}
        </Label>
        <textarea
          id="description"
          value={description}
          onChange={(e) => onUpdateField("description", e.target.value)}
          placeholder={t('templates.form.result_desc_placeholder')}
          disabled={!permissions.canEdit}
          className="w-full min-h-[80px] resize-none border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 rounded-md"
          required
        />
      </div>


      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-2">
          <Eye size={16} className="text-muted-foreground" />
          {t('templates.form.example_doc_label')}
        </Label>

        <Button
          variant="outline"
          className="flex items-center gap-2 relative"
          onClick={handleFileUpload}
          disabled={isUploading || !permissions.canEdit}
          title={t('templates.form.load_example_title')}
        >
          {isUploading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              {getUploadStageText()}
            </>
          ) : (
            <>
              <Upload size={16} />
              {t('templates.form.load_example_btn')}
            </>
          )}
        </Button>
      </div>

      {isUploading && (
        <div className="space-y-2">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{getUploadStageDescription()}</span>
            <span>{uploadProgress}%</span>
          </div>
          <Progress value={uploadProgress} className="h-2" />
        </div>
      )}


      <UnifiedMarkdownRenderer
        value={previewTemplateResult}
        onChange={(value) => onUpdateField("previewTemplateResult", value)}
        placeholder={t('templates.form.example_placeholder')}
        mode={permissions.canEdit ? "edit" : "view"}
        height={isMobile ? 300 : 200}
        className="w-full"
        forceUpdateKey={`${dataLoadedKey}-${fileUploadKey}`}
      />



      <AdvancedSettingsToggle
        isOpen={showAdvanced}
        onOpenChange={onShowAdvancedChange}
        prompt={prompt}
        onPromptChange={handlePromptChange}
        isMobile={isMobile}
        user={user}
        isPromptTouched={isPromptTouched}
        isAiInstructionDirty={isAiInstructionDirty}
        permissions={permissions}
        forceUpdateKey={dataLoadedKey}
      />
    </div>
  );
};

export default TemplateForm;
